import { create } from 'zustand';
import {
  MaterialTypeSchema,
  MaterialTypeRequest,
  UpdateMaterialTypeRequest,
  MaterialTypeQueryParams,
} from '../types/recycling.type';
import * as recyclingService from '../services/recycling.service';

interface MaterialTypeState {
  // Material Types
  materialTypes: MaterialTypeSchema[];
  materialTypesLoading: boolean;
  materialTypesError: string | null;
  totalMaterialTypes: number;
  materialTypesPage: number;
  materialTypesHasMore: boolean;
  materialTypesTotalPages: number;

  // Selected
  selectedMaterialType: MaterialTypeSchema | null;
  selectedMaterialTypeLoading: boolean;
  selectedMaterialTypeError: string | null;

  // Actions - Material Types
  fetchMaterialTypes: (organization_id: string, params?: MaterialTypeQueryParams) => Promise<void>;
  fetchMaterialType: (id: string) => Promise<void>;
  createMaterialType: (data: MaterialTypeRequest) => Promise<void>;
  updateMaterialType: (id: string, data: UpdateMaterialTypeRequest) => Promise<void>;
  deleteMaterialType: (id: string) => Promise<void>;
  setSelectedMaterialType: (item: MaterialTypeSchema | null) => void;
  clearMaterialTypesError: () => void;
  clearSelectedMaterialTypeError: () => void;
}

interface GenericResponse<T> {
  error?: {
    message?: string;
    code?: string;
  } | null;
  response?: {
    message?: string;
    code?: string;
  } | null;
  result: T;
}

export const useMaterialTypeStore = create<MaterialTypeState>((set, get) => ({
  // Material Types
  materialTypes: [],
  materialTypesLoading: false,
  materialTypesError: null,
  totalMaterialTypes: 0,
  materialTypesPage: 1,
  materialTypesHasMore: false,
  materialTypesTotalPages: 0,

  // Selected
  selectedMaterialType: null,
  selectedMaterialTypeLoading: false,
  selectedMaterialTypeError: null,

  // Actions - Material Types
  fetchMaterialTypes: async (organization_id: string, params?: MaterialTypeQueryParams) => {
    try {
      set({ materialTypesLoading: true, materialTypesError: null });
      const response = await recyclingService.getMaterialTypes(organization_id, params);
      const data = response as GenericResponse<MaterialTypeSchema[]>;
      set({
        materialTypes: data.result,
        totalMaterialTypes: data.result.length,
        materialTypesPage: 1,
        materialTypesHasMore: false,
        materialTypesTotalPages: 1,
        materialTypesLoading: false,
      });
    } catch (error) {
      set({
        materialTypesError: error instanceof Error ? error.message : 'Failed to fetch material types',
        materialTypesLoading: false,
      });
    }
  },
  fetchMaterialType: async (id: string) => {
    try {
      set({ selectedMaterialTypeLoading: true, selectedMaterialTypeError: null });
      const response = await recyclingService.getMaterialType(id);
      const data = response as GenericResponse<MaterialTypeSchema>;
      set({ selectedMaterialType: data.result, selectedMaterialTypeLoading: false });
    } catch (error) {
      set({
        selectedMaterialTypeError: error instanceof Error ? error.message : 'Failed to fetch material type',
        selectedMaterialTypeLoading: false,
      });
    }
  },
  createMaterialType: async (data: MaterialTypeRequest) => {
    try {
      set({ materialTypesLoading: true, materialTypesError: null });
      await recyclingService.createMaterialType(data);
      await get().fetchMaterialTypes(data.organization_id);
    } catch (error) {
      set({
        materialTypesError: error instanceof Error ? error.message : 'Failed to create material type',
        materialTypesLoading: false,
      });
    }
  },
  updateMaterialType: async (id: string, data: UpdateMaterialTypeRequest) => {
    try {
      set({ materialTypesLoading: true, materialTypesError: null });
      await recyclingService.updateMaterialType(id, data);
      if (get().selectedMaterialType?.id === id) {
        await get().fetchMaterialType(id);
      }
    } catch (error) {
      set({
        materialTypesError: error instanceof Error ? error.message : 'Failed to update material type',
        materialTypesLoading: false,
      });
    }
  },
  deleteMaterialType: async (id: string) => {
    try {
      set({ materialTypesLoading: true, materialTypesError: null });
      await recyclingService.deleteMaterialType(id);
      if (get().selectedMaterialType?.id === id) {
        set({ selectedMaterialType: null });
      }
    } catch (error) {
      set({
        materialTypesError: error instanceof Error ? error.message : 'Failed to delete material type',
        materialTypesLoading: false,
      });
    }
  },
  setSelectedMaterialType: (item) => {
    set({ selectedMaterialType: item });
  },
  clearMaterialTypesError: () => {
    set({ materialTypesError: null });
  },
  clearSelectedMaterialTypeError: () => {
    set({ selectedMaterialTypeError: null });
  },
})); 